/**
 * SIL-1471 (slice 1 of SIL-1470 arbitrage listings) — the seller-private
 * Notes panel on the owner's own listing detail page. Spec sections 3.4
 * and 8: plain text, visible only to members of the owning org, any seller
 * org (not just arbitrage/house listings). The caller is responsible for
 * only rendering this inside the owner view — see
 * `listings_.$listingId.tsx`'s `if (!listing.isOwner) return
 * <BuyerListingDetail .../>` guard above where this panel is mounted. There
 * is no additional ownership check in this component; the server is the
 * real boundary (listing_note RLS + the route's own 404-for-non-owner,
 * migration 980).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ListingNoteDTO } from '@siliconx/shared/dto/listing-note';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { formatDate } from '../../lib/format';
import {
  LISTING_NOTES_ADD_BUTTON,
  LISTING_NOTES_CANCEL_BUTTON,
  LISTING_NOTES_DELETE_BUTTON,
  LISTING_NOTES_DELETE_CONFIRM,
  LISTING_NOTES_EDIT_BUTTON,
  LISTING_NOTES_EMPTY,
  LISTING_NOTES_PLACEHOLDER,
  LISTING_NOTES_SAVE_BUTTON,
  LISTING_NOTES_SUBTITLE,
  LISTING_NOTES_TITLE,
} from '@siliconx/shared/ui/buyer-listing/copy/listingDetail';

const MAX_NOTE_LENGTH = 4000; // listing_note.body CHECK (980)

async function fetchNotes(listingId: string): Promise<ListingNoteDTO[]> {
  const res = await api.api.client.listings[':id'].notes.$get({ param: { id: listingId } });
  await throwIfNotOk(res as unknown as Response);
  const body = (await res.json()) as unknown as { data: ListingNoteDTO[] };
  return body.data;
}

function NoteComposer({
  listingId,
  onDone,
}: {
  listingId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.listings[':id'].notes.$post({
        param: { id: listingId },
        json: { body: body.trim() },
      });
      await throwIfNotOk(res as unknown as Response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.listings.notes(listingId) });
      setBody('');
      onDone();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Could not save note.'),
  });

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <textarea
        autoFocus
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={LISTING_NOTES_PLACEHOLDER}
        maxLength={MAX_NOTE_LENGTH}
        className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          disabled={createMutation.isPending}
          className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          {LISTING_NOTES_CANCEL_BUTTON}
        </button>
        <Button
          size="sm"
          variant="primary"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending || body.trim().length === 0}
        >
          {LISTING_NOTES_SAVE_BUTTON}
        </Button>
      </div>
    </div>
  );
}

function NoteRow({ listingId, note }: { listingId: string; note: ListingNoteDTO }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.listings[':id'].notes[':noteId'].$patch({
        param: { id: listingId, noteId: note.id },
        json: { body: body.trim() },
      });
      await throwIfNotOk(res as unknown as Response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.listings.notes(listingId) });
      setEditing(false);
    },
    onError: (err: Error) => toast.error(err.message ?? 'Could not save note.'),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.listings[':id'].notes[':noteId'].$delete({
        param: { id: listingId, noteId: note.id },
      });
      await throwIfNotOk(res as unknown as Response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.listings.notes(listingId) });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Could not delete note.'),
  });

  if (editing) {
    return (
      <li className="px-4 py-3">
        <textarea
          autoFocus
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={MAX_NOTE_LENGTH}
          className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setBody(note.body);
              setEditing(false);
            }}
            disabled={updateMutation.isPending}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {LISTING_NOTES_CANCEL_BUTTON}
          </button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending || body.trim().length === 0}
          >
            {LISTING_NOTES_SAVE_BUTTON}
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <p className="whitespace-pre-wrap text-sm text-foreground">{note.body}</p>
        {/* Edit/delete controls are UI-only convenience — the server (RLS,
            980) is the real author check, and a hijack attempt against
            another author's note 404s regardless of what this button does. */}
        {note.isOwnNote && (
          <div className="flex shrink-0 gap-2 text-xs">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-muted-foreground hover:text-foreground"
            >
              {LISTING_NOTES_EDIT_BUTTON}
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(LISTING_NOTES_DELETE_CONFIRM)) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="text-muted-foreground hover:text-destructive disabled:opacity-50"
            >
              {LISTING_NOTES_DELETE_BUTTON}
            </button>
          </div>
        )}
      </div>
      <div className="mt-2 text-xs text-muted-foreground">{formatDate(note.createdAt)}</div>
    </li>
  );
}

export function ListingNotesPanel({ listingId }: { listingId: string }) {
  const [composing, setComposing] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: qk.listings.notes(listingId),
    queryFn: () => fetchNotes(listingId),
  });

  const rows = data ?? [];

  return (
    <section>
      <PanelTitle
        title={LISTING_NOTES_TITLE}
        description={LISTING_NOTES_SUBTITLE}
        right={
          !composing && (
            <Button size="sm" variant="secondary" onClick={() => setComposing(true)}>
              {LISTING_NOTES_ADD_BUTTON}
            </Button>
          )
        }
      />

      <div className="mt-3 space-y-3">
        {composing && (
          <NoteComposer listingId={listingId} onDone={() => setComposing(false)} />
        )}

        {isLoading ? (
          <Card className="text-center text-sm text-muted-foreground">Loading…</Card>
        ) : rows.length === 0 && !composing ? (
          <Card className="text-center text-sm text-muted-foreground">{LISTING_NOTES_EMPTY}</Card>
        ) : rows.length > 0 ? (
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {rows.map((note) => (
              <NoteRow key={note.id} listingId={listingId} note={note} />
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
